// react
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';

// components
import Appeal from 'shared/components/Appeal/DefaultRenderer';
import ResponseFailure from 'shared/components/ResponseFailure';
import CongratsPopup from 'components/Organization/CongratsPopup';
import { EditManualOppForm } from '../EditManualOppForm/EditManualOppForm';
import PopupMain from 'components/Organization/Opportunities/OpportunityPopups/PopupMain';
import { SelectOption } from 'shared/components/Inputs/Select/types';

// redux
import { Store } from 'redux/root';
import { useDispatch, useSelector } from 'react-redux';
import { selectorGetUserId } from 'redux/user-service/selector';
import { resetError } from 'redux/error-service/action';
import { selectorGetHashtags } from 'redux/hashtag-service/selectors';
import { getAllHashtags, resetHashtags } from 'redux/hashtag-service/actions';
import { updateAdminOpportunity } from 'redux/opportunities-service/action';

// hooks
import { useLoading } from 'hooks/useLoading';
import useThunkDispatch from 'hooks/useThunkDispatch';

// helper
import { getFormInitialValues, getVolunteerFullName } from './helper';
import { getDiffOpportunities } from 'core/functions/diffOportunities';
import { API } from 'core/API';

// config
import { EDIT_ADMIN_MANUAL_OPPORTUNITY_INIT_VALUES } from './config';

// formik
import { Formik } from 'formik';

// types
import { IOpportunityResponse, IUpdateOpportunityRequest } from '@joc/api-gateway';
import {
	ORGANISATION_ACTIVE_STATUS,
	SearchUsersForAdminRequest,
	UserOrganizationsResponse,
} from '@joc/api-gateway/lib/api-client';

// validation
import { opportunityAdminManualValidationSchema } from 'core/validation';

// styles
import styles from './index.module.scss';

interface IEditManualOppPopup {
	generalTabClickHandler?: () => void;
	opportunityData?: IOpportunityResponse;
	setIsOpenPopup: Dispatch<SetStateAction<boolean>>;
}

export const EditManualOppPopup: FC<IEditManualOppPopup> = ({
	setIsOpenPopup,
	opportunityData,
	generalTabClickHandler,
}) => {
	const dispatch = useDispatch();
	const { startLoading } = useLoading();
	const thunkDispatch = useThunkDispatch();

	const orgHashtags = useSelector(selectorGetHashtags);
	const error = useSelector((store: Store) => store.errorRedux.error);
	const userId = useSelector((store: Store) => selectorGetUserId(store));

	const [isLoading, setIsLoading] = useState(false);
	const [isUpdated, setIsUpdated] = useState(false);
	const [initValues, setInitValues] = useState<any>(EDIT_ADMIN_MANUAL_OPPORTUNITY_INIT_VALUES);
	const [isVisibleHashtagField, setIsVisibleHashtagField] = useState<boolean>(false);
	const [organizations, setOrganizations] = useState<Array<SelectOption>>([]);

	useEffect(() => {
		const fetchUser = async () => {
			if (opportunityData?.user?.id) {
				const user = await API.userAdminSearch(
					SearchUsersForAdminRequest.fromJS({ userIds: [opportunityData.user.id] })
				);

				const orgsOptions =
					user.records[0].userOrganizations?.reduce(
						(acc: Array<SelectOption>, org: UserOrganizationsResponse) => {
							if (
								acc.some(option => option.value === org.organizationId) &&
								org.organization?.organizationActiveStatus.status ===
									ORGANISATION_ACTIVE_STATUS.SUSPENDED
							)
								return acc;
							return [
								...acc,
								{
									title: org.organization?.organizationName || '',
									value: org.organizationId || '',
								},
							];
						},
						[]
					) || [];
				setOrganizations(orgsOptions);
			}
		};

		if (opportunityData?.id) {
			const editManualOppInitValues = getFormInitialValues(opportunityData, userId);
			setInitValues(editManualOppInitValues);
			fetchUser().finally();
		}

		return () => {
			dispatch(resetHashtags);
		};
	}, []);

	useEffect(() => {
		if (opportunityData?.organisation?.id) {
			getAllOrgHashtags(opportunityData?.organisation?.id).finally();
		}
		setIsVisibleHashtagField(!!opportunityData?.tagId);
	}, [opportunityData]);

	const getAllOrgHashtags = async (orgId: string) => {
		await startLoading(getAllHashtags(orgId));
	};

	useEffect(() => {
		setIsVisibleHashtagField(!!orgHashtags.length && !!opportunityData?.tagId);
	}, [orgHashtags]);

	const handlerResetError = () => dispatch(resetError());

	const handleSubmit = async (values: any): Promise<void> => {
		setIsLoading(true);
		try {
			if (opportunityData?.id) {
				const editManualOppInitValues = getFormInitialValues(opportunityData, userId) as any;
				const body = document.createElement('body');
				body.innerHTML = values.opportunityDescription;
				const diff = getDiffOpportunities<IUpdateOpportunityRequest & { endTime: number }>(
					values,
					editManualOppInitValues
				);

				await thunkDispatch(
					updateAdminOpportunity(
						opportunityData?.id,
						{
							...values,
							smartGroupIds: null,
							organisationId: values.organisationId,
							opportunityDescription: values.opportunityDescription ? body.outerHTML : '',
						},
						Object.hasOwn(diff, 'endTime') || Object.hasOwn(diff, 'startDate')
					)
				);
				setIsUpdated(true);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			// dispatch(
			// 	setError(error?.response?.message.join(',') || error.message, error?.response?.code || error.code)
			// );
		} finally {
			setIsLoading(false);
		}
	};

	const handleSuccessSubmitForm = () => {
		if (generalTabClickHandler) {
			generalTabClickHandler();
		} else {
			setIsOpenPopup(false);
		}

		// dispatch(setIsNeedRefresh());
	};

	const volunteerName = useMemo(() => getVolunteerFullName(opportunityData), [getVolunteerFullName, opportunityData]);

	return (
		<PopupMain setIsShowPopup={setIsOpenPopup} isDisablePadding={true}>
			{error.state ? (
				<ResponseFailure
					message={error.message}
					buttonTitle="Check and try again"
					buttonClickHandler={handlerResetError}
				/>
			) : isUpdated ? (
				<CongratsPopup
					isFullHeight={true}
					title="Done"
					subtitle="Manual chesed opportunity has been updated."
					doneButtonClickHandler={handleSuccessSubmitForm}
				/>
			) : (
				<Appeal styleContact={true} isHeightDisable={true}>
					<div className={styles.title}>Edit Chesed Opportunity</div>
					{!!volunteerName && (
						<div className={styles.subtitle}>
							<span className={styles.subtitle__pre}>For:</span>
							{volunteerName}
						</div>
					)}
					<div className={styles.form__container}>
						<Formik
							enableReinitialize={true}
							initialValues={initValues}
							validationSchema={opportunityAdminManualValidationSchema}
							validateOnChange={true}
							onSubmit={values => {
								// @ts-ignore
								handleSubmit(values as any);
							}}
						>
							<EditManualOppForm
								organizations={organizations}
								options={orgHashtags}
								isLoading={isLoading}
								isVisibleHashtagField={isVisibleHashtagField}
								setIsVisibleHashtagField={setIsVisibleHashtagField}
								isPreviousCompleted={opportunityData?.isInitial}
							/>
						</Formik>
					</div>
				</Appeal>
			)}
		</PopupMain>
	);
};
